import { Box, CssBaseline, ThemeProvider, Toolbar } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import * as Sentry from "@sentry/react";
import React, { useCallback, useRef, useState } from "react";
import { NavigateOptions, Route, Routes, To, useLocation, useNavigate } from "react-router-dom";
import { Nullable } from "sonobello.utilities.react";

import Theme from "./assets/Theme";
import BackgroundContainer from "./App/Components/BackgroundContainer";
import Header from "./App/Components/Header";
import ResizingContainer from "./App/Components/ResizingContainer";
import EndedSessionView, { EndedSessionViewProps } from "./Views/Components/EndedSessionView";
import ExpiredReservationView, { IExpiredReservationViewProps } from "./Views/Components/ExpiredReservationView";
import ForbiddenSessionView, { ForbiddenSessionViewProps } from "./Views/Components/ForbiddenSessionView";
import InvalidLinkView from "./Views/Components/InvalidLinkView";
import NoAvailabilityView, { NoAvailabilityViewProps } from "./Views/Components/NoAvailabilityView";
import NonCandidateView from "./Views/Components/NonCandidateView";
import SomethingWentWrongView from "./Views/Components/SomethingWentWrongView";
import { Step, StepPathMap } from "./types/Step";
import { CachedViewProps, ViewConnector } from "./types/ViewConnector";
import { ViewKeyMap } from "./types/Views";
import { clearLocalStorage } from "./types/LocalStorage";
import GlobalStylesViewController from "./App/Components/GlobalStylesViewController";
import { HideUserWayStyle } from "./App/Components/HideUserWayStyle";
import LoadingExpander from "./App/Components/LoadingExpander";
import VersionWatermark from "./App/Components/VersionWatermark";
import AppContextAuthorizationRenewer from "./App/Compositions/AppContextAuthorizationRenewer";
import AppContextCachedDeviceInfoReporter from "./App/Compositions/AppContextCachedDeviceInfoReporter";
import AppContextDisqualifyingReasonsLoader from "./App/Compositions/AppContextDisqualifyingReasonsLoader";
import AppContextReportStepProgress from "./App/Compositions/AppContextReportStepProgress";
import AppContextStepProvider from "./App/Compositions/AppContextStepProvider";
import AppContextContactPatientQualificationCreator from "./App/Compositions/AppContextContactPatientQualificationCreator";
import AppErrorBoundary from "./App/Compositions/AppErrorBoundary";
import CalendarManager from "./Calendar/Compositions/CalendarManager";
import ComposedUserEventsHub from "./Events/Consumers/Compositions/ComposedUserEventsHub";
import ComposedLocationUserEventCreator from "./Events/Creators/Compositions/ComposedLocationUserEventCreator";
import { EventContextProvider } from "./Events/EventContext";
import ExperimentContextAppContextProvider from "./Experiment/Compositions/ExperimentContextAppContextProvider";
import ObxExperimentContextLoader from "./Experiment/Compositions/ObxExperimentContextLoader";
import ComposedPatientNoteUpdater from "./Notes/Compositions/ComposedRecordPatientNoteUpdater";
import SessionResolver from "./routing/Components/SessionResolver";
import AppContextAppRootRedirect from "./routing/Composition/AppContextAppRootRedirect";
import AppContextRedirectToFirstStep from "./routing/Composition/AppContextRedirectToFirstStep";
import ObxRoutes from "./routing/types/AppPaths";
import CenterSelector from "./steps/CenterSelect/Components/CenterSelector";
import AppContextCenterSelectStep from "./steps/CenterSelect/Compositions/AppContextCenterSelectStep";
import AppContextCenterSelectStepWrapper from "./steps/CenterSelect/Compositions/AppContextCenterSelectStepWrapper";
import CenterServiceModalSelector from "./steps/CenterSelect/Compositions/CenterServiceModalSelector";
import NextButtonDesktop from "./steps/Components/NextButtonDesktop";
import NextButtonMobile from "./steps/Components/NextButtonMobile";
import { IStepWrapperButtonProps } from "./steps/Components/StepWrapper";
import ComposedDiabeticMedicalStep from "./steps/Medical/Compositions/ComposedDiabeticMedicalStep";
import ComposedGeneralMedicalStep from "./steps/Medical/Compositions/ComposedGeneralMedicalStep";
import ComposedPacemakerMedicalStep from "./steps/Medical/Compositions/ComposedPacemakerMedicalStep";
import ComposedRadiationMedicalStep from "./steps/Medical/Compositions/ComposedRadiationMedicalStep";
import AppContextQualifyStep from "./steps/Qualify/Compositions/AppContextQualifyStep";
import AppContextSchedulingLoader from "./steps/Scheduling/Compositions/AppContextSchedulingLoader";
import HybridCalendarSchedulingStep from "./steps/Scheduling/Compositions/AppContextSchedulingStepWrapper";
import TelemetryClient from "./Telemetry/Compositions/TelemetryClient";
import ISession from "./types/ISession";
import ObxLoadSessionView from "./Views/LoadSession/Compositions/ObxLoadSessionView";
import { IThankYouViewProps } from "./Views/ThankYou/Components/ThankYouView";
import CachedThankYouView from "./Views/ThankYou/Compositions/CachedThankYouView";
import ComposedAllMedicalStep from "./steps/Medical/Compositions/ComposedAllMedicalStep";

/** A collection of the flows supported by this application. */

const defaultNextButtonConfiguration: IStepWrapperButtonProps = {
  NextButtonDesktop: NextButtonDesktop,
  NextButtonMobile: NextButtonMobile
};

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const App: React.FC = () => {
  const navigate = useNavigate();
  const navigateWithTelemetry = useCallback(
    (to: To, navOptions?: NavigateOptions) => TelemetryClient.traceNavigation(navigate, to, navOptions),
    [navigate]
  );

  const routerLocation = useLocation();
  const [sessionState, setSessionState] = useState<Nullable<ISession>>(null);
  const [viewPropsState, setViewPropsState] = useState<unknown | undefined>(CachedViewProps.loadFromCache());
  const clearSession = useCallback(() => {
    setSessionState(null);
    clearLocalStorage();
  }, []);
  const viewConnector = useRef(
    new ViewConnector(
      clearSession,
      navigateWithTelemetry,
      sessionKey => window.location.replace(`${window.location.origin}/${sessionKey}`),
      setViewPropsState
    )
  );
  const startSession = useCallback((session: ISession) => {
    setSessionState(session);
    navigateWithTelemetry(ObxRoutes.stepPrefix);
  }, []);

  return (
    <ThemeProvider theme={Theme}>
      <GlobalStylesViewController mobileStyles={[HideUserWayStyle]} desktopStyles={[]} />
      <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={"en-US"}>
        <CssBaseline />
        <Box height="100%" width="100%" display="flex" flexDirection="column">
          <Header pathName={routerLocation.pathname} />
          <Toolbar />
          <AppErrorBoundary
            contactId={sessionState?.contactId}
            flowId={sessionState?.flowId}
            bearerToken={sessionState?.token.token}
          >
            <Box component="main" display="flex" flexGrow={1}>
              <BackgroundContainer>
                <ResizingContainer>
                  <EventContextProvider>
                    <ComposedLocationUserEventCreator eventData={routerLocation.pathname} />
                    <ComposedUserEventsHub />
                    <SessionResolver
                      pathName={routerLocation.pathname}
                      router={viewConnector.current}
                      value={sessionState}
                      onChange={setSessionState}
                    >
                      <SentryRoutes>
                        <Route
                          path={ObxRoutes.stepPrefix}
                          element={
                            !sessionState ? (
                              <LoadingExpander />
                            ) : (
                              <ObxExperimentContextLoader
                                experimentIdentifier={sessionState.patient.id}
                                bearerAuthToken={sessionState.token.token}
                              >
                                <ExperimentContextAppContextProvider
                                  bearerAuthToken={sessionState.token.token}
                                  session={sessionState}
                                  pathName={routerLocation.pathname}
                                  viewConnector={viewConnector.current}
                                >
                                  <AppContextAppRootRedirect pathName={routerLocation.pathname}>
                                    <AppContextStepProvider
                                      pathName={routerLocation.pathname}
                                      router={viewConnector.current}
                                    />
                                    <AppContextDisqualifyingReasonsLoader />
                                    <ComposedPatientNoteUpdater />
                                    <AppContextCachedDeviceInfoReporter />
                                    <CalendarManager
                                      router={viewConnector.current}
                                      pathName={routerLocation.pathname}
                                    />
                                    <AppContextReportStepProgress pathName={routerLocation.pathname} />
                                    <AppContextAuthorizationRenewer router={viewConnector.current} />
                                    <AppContextContactPatientQualificationCreator />
                                  </AppContextAppRootRedirect>
                                </ExperimentContextAppContextProvider>
                              </ObxExperimentContextLoader>
                            )
                          }
                        >
                          <Route
                            path={StepPathMap[Step.qualify]}
                            element={<AppContextQualifyStep {...defaultNextButtonConfiguration} />}
                          />
                          <Route
                            path={StepPathMap[Step.medicalGeneral]}
                            element={<ComposedGeneralMedicalStep {...defaultNextButtonConfiguration} />}
                          />
                          <Route
                            path={StepPathMap[Step.medicalPacemaker]}
                            element={<ComposedPacemakerMedicalStep {...defaultNextButtonConfiguration} />}
                          />
                          <Route
                            path={StepPathMap[Step.medicalRadiation]}
                            element={<ComposedRadiationMedicalStep {...defaultNextButtonConfiguration} />}
                          />
                          <Route
                            path={StepPathMap[Step.medicalDiabetic]}
                            element={<ComposedDiabeticMedicalStep {...defaultNextButtonConfiguration} />}
                          />
                          <Route
                            path={StepPathMap[Step.medicalAll]}
                            element={<ComposedAllMedicalStep {...defaultNextButtonConfiguration} />}
                          />
                          <Route
                            path={StepPathMap[Step.centerSelection]}
                            element={
                              <AppContextSchedulingLoader>
                                <AppContextCenterSelectStepWrapper
                                  {...defaultNextButtonConfiguration}
                                  CenterSelectStep={props => (
                                    <AppContextCenterSelectStep {...props} CenterSelector={CenterSelector} />
                                  )}
                                  ServiceSelector={CenterServiceModalSelector}
                                />
                              </AppContextSchedulingLoader>
                            }
                          />
                          <Route
                            path={StepPathMap[Step.scheduling]}
                            element={
                              <AppContextSchedulingLoader>
                                <HybridCalendarSchedulingStep {...defaultNextButtonConfiguration} />
                              </AppContextSchedulingLoader>
                            }
                          />
                          <Route path="*" element={<AppContextRedirectToFirstStep />} />
                        </Route>
                        <Route
                          path={ViewKeyMap.thankYou.uri}
                          element={<CachedThankYouView viewProps={viewPropsState as IThankYouViewProps} />}
                        />
                        <Route
                          path={ViewKeyMap.completed.uri}
                          element={<ForbiddenSessionView {...(viewPropsState as ForbiddenSessionViewProps)} />}
                        />
                        <Route
                          path={ViewKeyMap.endedSession.uri}
                          element={<EndedSessionView {...(viewPropsState as EndedSessionViewProps)} />}
                        />
                        <Route
                          path={ViewKeyMap.noAvailability.uri}
                          element={<NoAvailabilityView {...(viewPropsState as NoAvailabilityViewProps)} />}
                        />
                        <Route path={ViewKeyMap.nonCandidate.uri} element={<NonCandidateView />} />
                        <Route
                          path={ViewKeyMap.expiredReservation.uri}
                          element={<ExpiredReservationView {...(viewPropsState as IExpiredReservationViewProps)} />}
                        />
                        <Route path={ViewKeyMap.invalidLink.uri} element={<InvalidLinkView />} />
                        <Route path={ViewKeyMap.somethingWentWrong.uri} element={<SomethingWentWrongView />} />
                        <Route
                          path="*"
                          element={
                            <ObxLoadSessionView
                              onChange={startSession}
                              router={viewConnector.current}
                              sessionKey={location.pathname.substring(1)}
                            />
                          }
                        />
                      </SentryRoutes>
                    </SessionResolver>
                  </EventContextProvider>
                </ResizingContainer>
              </BackgroundContainer>
            </Box>
          </AppErrorBoundary>
        </Box>
        <VersionWatermark />
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
